import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import mixpanel from "mixpanel-browser";

// Initialize Mixpanel with the token from environment variables
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN;

// Get the current platform (web, ios, android)
const platform = Capacitor.getPlatform();
const isNative = platform === "ios" || platform === "android";

const initMixpanel = () => {
  if (MIXPANEL_TOKEN) {
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV !== "production",
      track_pageview: true,
      persistence: "localStorage",
      ignore_dnt: true, // Ignore Do Not Track browser setting
      autocapture: true,
      record_sessions_percent: 100,
      record_mask_text_selector: "",
      record_block_selector: "",
      record_heatmap_data: true,
    });

    // Set up mobile app lifecycle listeners if on a mobile platform
    if (isNative) {
      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) {
          // App resumed from background
          trackEvent("App Resumed");
        } else {
          // App went to background
          trackEvent("App Paused");
        }
      });

      App.addListener("appUrlOpen", (data) => {
        // App opened via deep link
        trackEvent("Deep Link Opened", {
          url: data.url,
        });
      });
    }
  } else {
    console.warn("Mixpanel token not found. Analytics tracking is disabled.");
  }
};

/**
 * Track an event in Mixpanel
 * @param {string} name - Name of the event
 * @param {Object} props - Additional properties to track with the event
 */
const trackEvent = (name, props = {}) => {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.track(name, {
      ...props,
      platform,
      isNative,
    });
  } catch (error) {
    console.error("Error tracking Mixpanel event:", error);
  }
};

/**
 * Track a page view in Mixpanel
 * @param {string} pageName - Name of the page
 * @param {Object} props - Additional properties to track with the event
 */
const trackPageView = (pageName, props = {}) => {
  trackEvent("Page View", {
    page: pageName,
    ...props,
  });
};

/**
 * Identify a user in Mixpanel
 * @param {string} userId - Unique identifier for the user
 * @param {Object} traits - User traits/properties
 */
const identifyUser = (userId, traits = {}) => {
  if (!MIXPANEL_TOKEN || !userId) return;

  try {
    mixpanel.identify(userId);

    if (Object.keys(traits).length > 0) {
      mixpanel.people.set({
        ...traits,
        platform,
        isNative,
        $last_seen: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error("Error identifying user in Mixpanel:", error);
  }
};

/**
 * Reset the current user in Mixpanel (for logout)
 */
const resetUser = () => {
  if (!MIXPANEL_TOKEN) return;

  try {
    mixpanel.reset();
  } catch (error) {
    console.error("Error resetting Mixpanel user:", error);
  }
};

// Initialize Mixpanel immediately for backward compatibility
if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    debug: process.env.NODE_ENV !== "production",
    track_pageview: true,
    persistence: "localStorage",
    ignore_dnt: true, // Ignore Do Not Track browser setting
    autocapture: true,
    record_sessions_percent: 100,
    record_mask_text_selector: "",
    record_block_selector: "",
    record_heatmap_data: true,
  });
}

// For backward compatibility with existing code
export const track = trackEvent;

export {
  identifyUser,
  initMixpanel,
  isNative,
  platform,
  resetUser,
  trackEvent,
  trackPageView,
};

export default {
  identifyUser,
  initMixpanel,
  resetUser,
  track: trackEvent,
  trackEvent,
  trackPageView,
};
